import type { LucideIcon } from 'lucide-react';

export type FilterOperator = 'is' | 'isNot';

export interface FilterOption {
  value: string;
  label: string;
  icon?: LucideIcon;
  /** Indent this option in the value submenu; useful for lightweight parent/child lists. */
  indent?: number;
  /** Custom selection state for options that coordinate sibling filters. */
  isSelected?: (state: FilterState) => boolean;
  /** Custom selection action for options that coordinate sibling filters. */
  onSelect?: (state: FilterState) => FilterState;
  /**
   * Optional count to render next to the value. Consumers compute this
   * dynamically based on the other active filters so the picker reflects
   * how many rows each value would yield if added.
   */
  count?: number;
}

export type TextSearchSuggestion = {
  value: string;
  label: string;
  count?: number;
};

/**
 * A relative time preset (e.g. "Last 2 hours") for the `time-range` filter
 * kind. `resolve(now)` runs at query-build time, which is what makes
 * presets *live* — the URL stores the slug (`?id=last_2h`) and reopening
 * the page tomorrow shows tomorrow's last-2-hour window.
 */
export interface TimeRangePreset {
  /** URL slug. Keep stable — saved views reference it by name. */
  id: string;
  label: string;
  resolve: (now: Date) => { from: Date; to: Date };
}

export type FilterConfig =
  | {
      kind: 'multi-select';
      id: string;
      label: string;
      icon?: LucideIcon;
      options: FilterOption[];
      /** When false, hide the chip's `is` / `is not` operator toggle. */
      allowOperatorToggle?: boolean;
      /** Show the attribute immediately while its dynamic option list loads. */
      loading?: boolean;
      defaultOperator?: FilterOperator;
      /** Custom active count for chips that coordinate sibling filters. */
      activeCount?: (state: FilterState, active: ActiveFilter | undefined) => number;
      /** Custom chip summary for chips that coordinate sibling filters. */
      formatSummary?: (state: FilterState, active: ActiveFilter) => string;
      /** Remove these sibling filters when this chip is removed. */
      removeWith?: string[];
    }
  | {
      kind: 'text-search';
      id: string;
      label: string;
      icon?: LucideIcon;
      placeholder?: string;
      /** Optional datalist-style suggestions shown while typing */
      suggestions?: TextSearchSuggestion[];
      debounceMs?: number;
      defaultOperator?: FilterOperator;
    }
  | {
      kind: 'date-range';
      id: string;
      label: string;
      icon?: LucideIcon;
      defaultOperator?: FilterOperator;
    }
  | {
      kind: 'numeric-range';
      id: string;
      label: string;
      icon?: LucideIcon;
      placeholder?: string;
      min?: number;
      max?: number;
      step?: number;
      /** Shown in chip label after values (e.g. "%"). */
      unit?: string;
      /** When true, only a minimum bound is collected (`values[1]` stays empty). */
      minOnly?: boolean;
      debounceMs?: number;
      defaultOperator?: FilterOperator;
    }
  | {
      /**
       * Combined preset + absolute time-range filter. One chip; two modes:
       *   - Preset:   `values = [<preset.id>]` (single value)
       *   - Absolute: `values = [<from>, <to>]` (two values, format consumer-defined)
       *
       * URL kind must be 'time-range' for the encoder/decoder to round-trip
       * cleanly. See [urlState.ts](./utils/urlState.ts).
       */
      kind: 'time-range';
      id: string;
      label: string;
      icon?: LucideIcon;
      /** Preset list shown above the absolute range picker. Order preserved. */
      presets: TimeRangePreset[];
      /**
       * Short timezone label rendered next to the datetime-local inputs
       * (e.g. "PT"). The submenu does not enforce a timezone — the
       * consumer's `parseLocalDateTime` / `formatLocalDateTime` callbacks
       * own that semantics. This is purely a UI hint.
       */
      timezoneLabel?: string;
      /**
       * Convert a `<input type="datetime-local">` string (`YYYY-MM-DDTHH:mm`)
       * to the storage format the consumer puts in `values[]`. Defaults to
       * identity. Return empty string to clear that bound.
       */
      parseLocalDateTime?: (local: string) => string;
      /**
       * Inverse of `parseLocalDateTime`: convert a stored value back into a
       * `<input type="datetime-local">` string for display. Defaults to
       * identity. Return empty string when input cannot be parsed.
       */
      formatLocalDateTime?: (stored: string) => string;
      defaultOperator?: FilterOperator;
    };

export interface ActiveFilter {
  attributeId: string;
  operator: FilterOperator;
  values: string[];
}

export interface FilterState {
  filters: ActiveFilter[];
}

const EMPTY_FILTERS = Object.freeze([]) as unknown as ActiveFilter[];

export const EMPTY_FILTER_STATE: FilterState = Object.freeze({
  filters: EMPTY_FILTERS,
}) as FilterState;
